import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Flag, AlertTriangle, Send, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Layout } from '@/components/wiki/Layout';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReportPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const pageId = searchParams.get('page');
  
  const [page, setPage] = useState(null);
  const [issueType, setIssueType] = useState('');
  const [description, setDescription] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [reporterEmail, setReporterEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (pageId) {
      const fetchPage = async () => {
        try {
          const response = await axios.get(`${API}/pages/id/${pageId}`);
          setPage(response.data);
        } catch (error) {
          console.error('Error fetching page:', error);
        }
      };
      fetchPage();
    }
  }, [pageId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!issueType || !description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reports`, {
        page_id: pageId || null,
        file_url: fileUrl.trim() || null,
        issue_type: issueType,
        description: description.trim(),
        reporter_email: reporterEmail.trim() || null
      });
      
      toast.success('Report submitted successfully. The content has been temporarily removed for review.');
      navigate('/');
    } catch (error) {
      toast.error('Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8" data-testid="report-page">
        <Link to="/" className="inline-flex items-center gap-2 text-[#36c] hover:underline mb-6">
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#fee7e6] text-[#d33] mb-4">
            <Flag className="h-8 w-8" />
          </div>
          <h1 className="font-serif text-3xl font-bold text-[#202122] mb-2">
            Report a Violation
          </h1>
          <p className="text-[#54595d]">
            Help us maintain a safe and trustworthy community by reporting content that violates our Terms of Use.
          </p>
        </div>

        {/* Warning */}
        <div className="bg-[#fef6e7] border border-[#fc3] rounded-sm p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-[#ac6600] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-[#ac6600] font-medium">Important Notice</p>
              <p className="text-sm text-[#54595d] mt-1">
                Reported content will be temporarily removed pending review by our moderation team. 
                False reports may result in account suspension.
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-sm border border-[#a2a9b1] p-8">
          {page && (
            <div className="mb-6 p-4 bg-[#f8f9fa] rounded-sm border border-[#c8ccd1]">
              <p className="text-sm text-[#54595d]">Reporting page:</p>
              <p className="font-medium text-[#202122]">{page.title}</p>
            </div>
          )}

          <div className="space-y-6">
            {!pageId && (
              <div>
                <Label htmlFor="file-url" className="text-[#202122]">Page URL or File URL</Label>
                <Input
                  id="file-url"
                  placeholder="https://..."
                  value={fileUrl}
                  onChange={(e) => setFileUrl(e.target.value)}
                  className="mt-1 bg-white border-[#a2a9b1] text-[#202122] placeholder:text-[#72777d]"
                  data-testid="file-url-input"
                />
              </div>
            )}

            <div>
              <Label htmlFor="issue-type" className="text-[#202122]">Issue Type *</Label>
              <Select value={issueType} onValueChange={setIssueType}>
                <SelectTrigger className="mt-1 bg-white border-[#a2a9b1] text-[#202122]" data-testid="issue-type-select">
                  <SelectValue placeholder="Select issue type..." />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#a2a9b1]">
                  <SelectItem value="copyright" className="text-[#202122] hover:bg-[#eaecf0]">Copyright Infringement</SelectItem>
                  <SelectItem value="privacy" className="text-[#202122] hover:bg-[#eaecf0]">Privacy Violation</SelectItem>
                  <SelectItem value="defamation" className="text-[#202122] hover:bg-[#eaecf0]">Defamation / Libel</SelectItem>
                  <SelectItem value="harassment" className="text-[#202122] hover:bg-[#eaecf0]">Harassment</SelectItem>
                  <SelectItem value="other" className="text-[#202122] hover:bg-[#eaecf0]">Other Violation</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="description" className="text-[#202122]">Description *</Label>
              <Textarea
                id="description"
                placeholder="Please describe the violation in detail..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                className="mt-1 bg-white border-[#a2a9b1] text-[#202122] placeholder:text-[#72777d]"
                data-testid="description-input"
              />
            </div>

            <div>
              <Label htmlFor="reporter-email" className="text-[#202122]">Your Email (optional)</Label>
              <Input
                id="reporter-email"
                type="email"
                placeholder="your@email.com"
                value={reporterEmail}
                onChange={(e) => setReporterEmail(e.target.value)}
                className="mt-1 bg-white border-[#a2a9b1] text-[#202122] placeholder:text-[#72777d]"
                data-testid="reporter-email-input"
              />
              <p className="text-xs text-[#54595d] mt-1">
                Provide your email if you'd like to receive updates about this report.
              </p>
            </div>

            <Button 
              type="submit" 
              disabled={submitting}
              className="w-full bg-[#d33] hover:bg-[#b32424] text-white"
              data-testid="submit-report-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              {submitting ? 'Submitting...' : 'Submit Report'}
            </Button>
          </div>
        </form>

        <p className="text-center mt-6 text-sm text-[#54595d]">
          By submitting this report, you confirm that you understand our{' '}
          <Link to="/terms" className="text-[#36c] hover:underline">Terms of Use</Link>.
        </p>
      </div>
    </Layout>
  );
}
